import { DefaultNodeTypes } from '@payloadcms/richtext-lexical'
import { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react'
import { textConverter } from './textConverter'
import React from 'react'
import { ExternalLink } from 'lucide-react'
import { extractYouTubeVideoId, isStandaloneYouTubeText } from '@/lib/youtube'
import { YouTubeEmbed } from '../../YouTubeEmbed'

type NodeTypes = DefaultNodeTypes

type LexicalChild = {
  type: string
  text?: string
  children?: LexicalChild[]
  fields?: { url?: string; newTab?: boolean }
  url?: string
}

function getLinkPlainText(children: LexicalChild[] | undefined): string {
  if (!children?.length) return ''
  return children
    .map((child) => {
      if (child.type === 'text' && child.text) return child.text
      if (child.children) return getLinkPlainText(child.children)
      return ''
    })
    .join('')
    .trim()
}

function getLinkHref(node: LexicalChild): string {
  return node.fields?.url || node.url || getLinkPlainText(node.children) || ''
}

function isStandaloneYouTubeLink(href: string, linkText: string): boolean {
  const videoId = extractYouTubeVideoId(href)
  if (!videoId) return false
  if (!linkText) return true
  if (extractYouTubeVideoId(linkText) === videoId) return true
  return linkText.replace(/\s/g, '') === href.replace(/\s/g, '')
}

function getYouTubeIdFromParagraphNode(node: LexicalChild): string | null {
  const text = getLinkPlainText(node.children)
  if (isStandaloneYouTubeText(text)) return extractYouTubeVideoId(text)

  const children = node.children || []
  if (children.length === 1) {
    const child = children[0]
    if (child.type === 'link' || child.type === 'autolink') {
      const href = getLinkHref(child)
      const linkText = getLinkPlainText(child.children)
      if (isStandaloneYouTubeLink(href, linkText)) return extractYouTubeVideoId(href)
    }
  }

  return null
}

export const jsxConverter: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...textConverter,
  paragraph: (args) => {
    const videoId = getYouTubeIdFromParagraphNode(args.node as LexicalChild)
    if (videoId) {
      return React.createElement(YouTubeEmbed, {
        videoId,
        title: 'YouTube video',
        className: 'my-8',
      })
    }
    const defaultParagraph = defaultConverters.paragraph
    if (typeof defaultParagraph === 'function') {
      return defaultParagraph(args)
    }
    return null
  },
  link: ({ node, nodesToJSX }) => {
    const linkNode = node as LexicalChild
    const href = getLinkHref(linkNode)
    const linkText = getLinkPlainText(linkNode.children)
    const videoId = extractYouTubeVideoId(href)

    if (videoId && isStandaloneYouTubeLink(href, linkText)) {
      return React.createElement(YouTubeEmbed, {
        videoId,
        title: 'YouTube video',
        className: 'my-8',
      })
    }

    const isExternal = linkNode.fields?.newTab || href.startsWith('http')
    const children = nodesToJSX({
      nodes: node.children || [],
    })

    return React.createElement(
      'a',
      {
        href,
        target: isExternal ? '_blank' : undefined,
        rel: isExternal ? 'noopener noreferrer' : undefined,
        className:
          'text-brand-gold underline underline-offset-2 decoration-brand-gold/40 hover:decoration-brand-gold hover:text-brand-gold-light transition-colors inline-flex items-center gap-0.5',
      },
      children,
      isExternal
        ? React.createElement(ExternalLink, {
            className: 'inline w-3.5 h-3.5 shrink-0 ml-0.5',
            'aria-hidden': 'true',
          })
        : null,
    )
  },
})
